/**
 * The chunks of a file (docs/FORMAT.md §2.2). Each chunk of up to 4 MiB is sealed on its own under the file's key, with an IV
 * that is derived from its index (STREAM) and additional data that binds it to its file, position, count and the last-chunk
 * flag: a server that reorders, swaps, drops or truncates chunks makes decryption fail instead of producing a different
 * file. Cutting a `File` into chunks and putting the pieces back into a `Blob` is `lib/files.ts`.
 */
import { toBase64Url, toHex } from './base64url';
import { chunkAad, chunkFlags, chunkIv, checkHead } from './envelope';
import { CHUNK_OVERHEAD, ENVELOPE_HEAD_BYTES, FILE_NONCE_BYTES, SUITE_AES_GCM, VERSION } from './labels';
import { concat, DecryptError, openWithIv, sealWithIv, sha256 } from './primitives';
import { randomBytes } from './random';
import { fileKey } from './subkeys';

/** The random value a new file's chunks, header and key share. */
export function newFileNonce(): string {
  return toBase64Url(randomBytes(FILE_NONCE_BYTES));
}

function checkPosition(index: number, count: number): void {
  if (!Number.isInteger(count) || count < 1 || !Number.isInteger(index) || index < 0 || index >= count) throw new RangeError(`No chunk ${String(index)} of ${String(count)}`);
}

/**
 * Seals the chunks of one file. A chunk's IV is its index, so sealing an index twice under other bytes would use one key and
 * one IV for two plaintexts; the sealer remembers a digest of what each index held and refuses a different plaintext, as
 * when a file changes while it is being sent.
 */
export class ChunkSealer {
  private readonly digests = new Map<number, Promise<string>>();

  constructor(
    private readonly linkKey: CryptoKey,
    private readonly nonce: string,
    private readonly count: number,
  ) {}

  async seal(index: number, plain: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
    checkPosition(index, this.count);
    // the digest of what an index holds is recorded before anything is awaited, so two seals of one index at once cannot both pass
    const before = this.digests.get(index);
    const mine = sha256(plain).then(toHex);
    this.digests.set(index, before ?? mine);
    const [expected, digest] = await Promise.all([before ?? mine, mine]);
    if (expected !== digest) throw new DecryptError('The file changed while it was being sent.');
    const last = index === this.count - 1;
    const flags = chunkFlags(last);
    const sealed = await sealWithIv(await fileKey(this.linkKey, this.nonce), chunkIv(index, last), plain, chunkAad(this.nonce, index, this.count, flags));
    return concat(Uint8Array.of(VERSION, SUITE_AES_GCM, flags), sealed);
  }
}

export async function openChunk(linkKey: CryptoKey, data: Uint8Array<ArrayBuffer>, nonce: string, index: number, count: number): Promise<Uint8Array<ArrayBuffer>> {
  checkPosition(index, count);
  const { flags } = checkHead(data, CHUNK_OVERHEAD);
  const last = index === count - 1;
  // the last chunk of the count, and only it, carries the flag
  if (flags !== chunkFlags(last)) throw new DecryptError();
  return openWithIv(await fileKey(linkKey, nonce), chunkIv(index, last), data.slice(ENVELOPE_HEAD_BYTES), chunkAad(nonce, index, count, flags));
}
